import posthog from 'posthog-js';
import { getPersistentPlayerId } from './socket';

let initialized = false;
const excluded = () => window.location.pathname.startsWith('/admin') || import.meta.env.DEV;
const cleanPath = (path: string) => path.replace(/^\/room\/[^/]+/, '/room/:roomCode');
const cleanUrl = (value: string) => {
  try {
    const url = new URL(value);
    // Keep campaign attribution, but never arbitrary query strings or room codes.
    const query = new URLSearchParams();
    for (const key of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term']) {
      const item = url.searchParams.get(key);
      if (item) query.set(key, item.slice(0, 150));
    }
    return `${url.origin}${cleanPath(url.pathname)}${query.size ? `?${query}` : ''}`;
  } catch { return ''; }
};

export const initPostHog = () => {
  if (typeof window === 'undefined' || initialized || excluded() || !import.meta.env.VITE_POSTHOG_KEY) return;
  posthog.init(import.meta.env.VITE_POSTHOG_KEY, {
    api_host: import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com',
    person_profiles: 'identified_only',
    autocapture: false,
    capture_pageview: false,
    capture_pageleave: true,
    disable_session_recording: true,
    disable_external_dependency_loading: true,
    advanced_disable_flags: true,
    before_send: event => {
      if (!event || excluded()) return null;
      // The SDK repeats URL metadata at event, session and person levels.
      for (const fields of [event.properties, event.$set, event.$set_once, event.properties.$set, event.properties.$set_once]) {
        if (!fields || typeof fields !== 'object') continue;
        for (const [key, value] of Object.entries(fields)) {
          if (typeof value !== 'string') continue;
          if (/(?:url|referrer)$/.test(key)) fields[key] = cleanUrl(value);
          else if (/pathname$/.test(key)) fields[key] = cleanPath(value);
        }
      }
      event.properties.$pathname = cleanPath(window.location.pathname);
      return event;
    },
  });
  initialized = true;
  posthog.register({ schema_version: 2, environment: 'production', platform: 'web', entity_type: 'player', event_source: 'client' });
  posthog.identify(getPersistentPlayerId());
};

export const trackEvent = (event: string, properties?: Record<string, unknown>) => {
  if (typeof window === 'undefined' || excluded()) return;
  try {
    initPostHog();
    if (initialized) posthog.capture(event, properties);
  } catch { /* Analytics must not interrupt a player action. */ }
};

export const trackPageView = (path: string) => {
  if (typeof window === 'undefined') return;
  trackEvent('$pageview', { $current_url: cleanUrl(window.location.href), path: cleanPath(path) });
};
